import { useEffect, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  ConversionFunnelDaySchema,
  type ConversionFunnelDay,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/funnel/useFunnel';

export type FunnelState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  /** Nenhum doc no formato atual dentro do período (ver `EMPTY` no page). */
  | { kind: 'EMPTY' }
  | { kind: 'READY'; day: ConversionFunnelDay };

/**
 * Valida um documento cru do Firestore.
 *
 * Devolve `null` para qualquer coisa que não seja o formato atual —
 * inclusive os documentos v1 já gravados, que tinham nove etapas com três
 * delas zeradas por construção. Renderizá-los no chart novo produziria um
 * funil parte real, parte inventado, sem nada na tela indicando isso.
 *
 * Exportada para teste: é a única decisão do hook que não é I/O.
 */
export function parseFunnelDay(raw: unknown): ConversionFunnelDay | null {
  const parsed = ConversionFunnelDaySchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Lê **um** documento de `/aggregations/funnel/days/{yyyymmdd}`: o mais
 * recente dentro do período escolhido.
 *
 * ## Por que um, e não a soma do período
 *
 * A versão anterior somava os dias. Fazia sentido quando cada doc era uma
 * contagem independente do dia — e era justamente por isso que o resultado
 * não era um funil. Hoje cada doc já é o funil de uma janela de 30 dias
 * terminando naquele dia, calculado por pessoa pelo PostHog. Somar dois
 * desses contaria a mesma pessoa duas vezes: as janelas se sobrepõem.
 *
 * Então o período do dashboard escolhe **em que dia olhar**, e o funil
 * mostrado é o dos 30 dias anteriores a ele.
 *
 * Documentos no schema antigo (v1, nove etapas com três delas zeradas por
 * construção) não passam no parse e são ignorados de propósito — misturá-los
 * com o formato novo produziria um funil parte real, parte inventado.
 *
 * Origem: sem issue — commit 2c50700f6, 2026-05-04 (rota /dashboard/funnel);
 * reescrito em 876a0c3dc (#69, 2026-08-30) para o funil por pessoa.
 *
 * Contrato: `period` → `FunnelState`, leitura one-shot. Nenhum doc no
 * período ou doc fora do v2 → `EMPTY` (este com `console.warn`); erro de
 * leitura → `ERROR` só com `console.error`, sem report ao PostHog (achado
 * registrado na #926).
 *
 * Usado por: `FunnelDashboardPage.tsx` (rota `/dashboard/funnel`; web
 * `apps/admin`, super-admin).
 *
 * Arquitetura: só leitura de coleção direta, via
 * `match /aggregations/{type}/{subcoll}/{id}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`); intervalo e ordem pelo `documentId()` não pedem
 * índice composto. Escritor: o scheduler `aggregateFunnel` (Functions), que
 * calcula pelo PostHog.
 *
 * Dados (anonimizado; `ConversionFunnelDaySchema` em
 * `packages/types/src/funnel.ts`):
 *   aggregations/funnel/days/{yyyymmdd}
 *     { "yyyymmdd": "20261007", "schemaVersion": 2, "windowDays": 30,
 *       "conversionWindowDays": 14, "bySurface": { "android": { … },
 *       "web": { … } }, "computedAt": 1759300000000,
 *       "computedBy": "aggregateFunnel" }
 *
 * Issues: #69 (funil por pessoa), #926.
 */
export function useFunnel(period: Period): FunnelState {
  const [state, setState] = useState<FunnelState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });
    const fromCompact = period.from.replace(/-/g, '');
    const toCompact = period.to.replace(/-/g, '');

    async function load(): Promise<void> {
      try {
        const ref = collection(firestore, 'aggregations', 'funnel', 'days');
        const snap = await getDocs(
          query(
            ref,
            where(documentId(), '>=', fromCompact),
            where(documentId(), '<=', toCompact),
            orderBy(documentId(), 'desc'),
            // Só o dia mais recente do período interessa; os anteriores
            // cobrem janelas sobrepostas do mesmo funil.
            limit(1),
          ),
        );
        if (guard.cancelled) return;

        const doc = snap.docs[0];
        if (doc === undefined) {
          setState({ kind: 'EMPTY' });
          return;
        }
        const day = parseFunnelDay(doc.data());
        if (day === null) {
          // Doc no formato antigo, ou corrompido. Não é erro de leitura:
          // mostrar "sem dado" é mais honesto que renderizar meio funil.
          console.warn(`${TAG}: ${doc.id} ignorado (schema não é o v2)`);
          setState({ kind: 'EMPTY' });
          return;
        }
        setState({ kind: 'READY', day });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_funnel_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [period.from, period.to]);

  return state;
}
